import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import { toast } from '@/lib/notifications';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { ALL_COUNTRIES_WILDCARD, Capability, Sku, type AddonConfig } from '@etiquetachef/types';
import { AddonCountriesPicker } from './AddonCountriesPicker';
import {
  createAddonCallable,
  setAddonEnabledCountriesCallable,
  updateAddonCallable,
} from '@/lib/callables';

const LOCALES = ['pt-BR', 'en', 'es', 'it'] as const;
type Locale = (typeof LOCALES)[number];

type I18nQuad = Record<Locale, string>;

/** Chave i18n do nome de cada idioma (`billing.addons.dialog.locales.*`) — rótulo visível do bloco (#954). */
const LOCALE_LABEL_KEY: Record<Locale, string> = {
  'pt-BR': 'billing.addons.dialog.locales.ptBR',
  en: 'billing.addons.dialog.locales.en',
  es: 'billing.addons.dialog.locales.es',
  it: 'billing.addons.dialog.locales.it',
};

type Mode = 'create' | 'edit';

type Props = {
  open: boolean;
  mode: Mode;
  /** No modo edit, vem populado; em create, null. */
  addon: AddonConfig | null;
  onClose: () => void;
  /** Disparado após createAddon OK. Desde a #679 F3 a `AddonsPage` só fecha o
   *  diálogo: o piso mora no CRM (sem preço de lista desde a #964), não em `/billing/plan-prices`. */
  onCreated?: (sku: string) => void;
};

const SKU_REGEX = /^[A-Z][A-Z0-9_]{2,30}$/;

const EMPTY_I18N: I18nQuad = { 'pt-BR': '', en: '', es: '', it: '' };

/**
 * Diálogo unificado de criar/editar add-on (`/billing/addons`). Em `create`
 * chama `createAddon` (Stripe Products idempotentes + doc); em `edit` chama
 * `updateAddon` (i18n/capabilities) e, só se a lista de países mudou,
 * `setAddonEnabledCountries` (separado para o audit diff ser granular).
 *
 * Origem: sem issue — commit 1bc01f1b9, 2026-05-25 23:58 +0200 (Sprint 35, PR G1).
 *
 * Contrato: validação local espelha o schema (SKU `^[A-Z][A-Z0-9_]{2,30}$` e
 * ≠ `BASE`; nome ≥ 2 e descrição ≥ 10 caracteres nos 4 locales). SKU editável
 * só em `create`. `required_capabilities` é sempre `[BASE_ACTIVE]` — a lista de
 * escolha saiu na F4 da #679, quando `SUPPLIER_INTEGRATION` deixou de ser
 * capability nomeada. Erros do callable são lidos pelo `code`
 * (`already-exists` → SKU existe; `not-found`), nunca pela mensagem.
 * Só cria a "casca": o piso mora no CRM (`crm_appConfig/
 * pricingFloors`), não em `/billing/plan-prices` (#679 F3).
 * Países: criar começa em "todos os países" (`["*"]`, #954); desligando, o
 * universo é a lista de locais da Google Play (`ALL_PLAY_LOCATIONS`). O
 * servidor aceita `["*"]` ou qualquer ISO alpha-2 (`AddonCountriesSchema`).
 *
 * Os três callables exigem no servidor `ADMIN_PERMISSIONS.manageAddons`
 * (ROOT/FINANCE, `apps/functions/src/auth/permissions.ts`).
 *
 * Usado por: `routes/AddonsPage.tsx` (rota `/billing/addons`, menu Cobrança →
 * Add-ons; spec `docs/screens/billing-addons.md`), admin web.
 *
 * Dados: `/config/addons/items/{sku}` (`AddonConfigSchema`,
 * `packages/types/src/schemas.ts`); inputs `CreateAddonInputSchema`,
 * `UpdateAddonInputSchema`, `SetAddonEnabledCountriesInputSchema`. `name_i18n` e
 * `description_i18n` têm exatamente pt-BR/en/es/it (sem fr).
 *
 * Falhas conhecidas:
 *  - 2026-09-24 (#710, auditoria F3): "SKU já existe" nunca aparecia — o
 *    discriminador era lido da mensagem e não de `err.code`.
 *  - Até 2026-10-13 (#935, item 8): editar gravava em duas chamadas
 *    (`updateAddon` + `setAddonEnabledCountries`); falha da segunda deixava a
 *    edição pela metade. Hoje os países vão no próprio `updateAddon`.
 *
 * Histórico:
 *  - 2026-06-08 21:19 +0200 — Cloudscape (39-E).
 *  - 2026-09-24 13:51 +0200 — (#710) erro do callable pelo `code`.
 *  - 2026-09-24 21:27 +0200 — (#714) `onCreated` só fecha: preço saiu do admin.
 *  - 2026-10-10 14:40 +0200 — (#798) rota, menu, permissão; referência ao
 *    `CouponEditorDialog` (que não existe mais) corrigida (#796).
 *  - 2026-10-13 08:47 +0200 — (#954) países da Google Play por continente
 *    (`AddonCountriesPicker`), todos marcados ao criar; rótulo de idioma em
 *    cada bloco de nome/descrição; seções com cabeçalho.
 *  - 2026-10-13 09:27 +0200 — (#954) criar nasce com `["*"]` (todos os países).
 *  - 2026-10-13 11:10 +0200 — (#935, item 8) edição numa chamada só; falha
 *    reportada ao PostHog (item 14).
 */
export function AddonEditorDialog({
  open,
  mode,
  addon,
  onClose,
  onCreated,
}: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [sku, setSku] = useState('');
  const [nameI18n, setNameI18n] = useState<I18nQuad>(EMPTY_I18N);
  const [descI18n, setDescI18n] = useState<I18nQuad>(EMPTY_I18N);
  const [countries, setCountries] = useState<Set<string>>(new Set());
  const [capabilities, setCapabilities] = useState<Set<string>>(
    new Set([Capability.BASE_ACTIVE]),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && addon) {
      setSku(addon.sku);
      setNameI18n(addon.name_i18n);
      setDescI18n(addon.description_i18n);
      setCountries(new Set(addon.enabled_countries));
      setCapabilities(new Set(addon.required_capabilities));
    } else {
      setSku('');
      setNameI18n(EMPTY_I18N);
      setDescI18n(EMPTY_I18N);
      // #954: add-on novo nasce em "todos os países" (`["*"]`, owner
      // 2026-10-13) — quem restringe é o console da Play. Era `new Set()`: o
      // add-on nascia invisível em todo lugar.
      setCountries(new Set([ALL_COUNTRIES_WILDCARD]));
      setCapabilities(new Set([Capability.BASE_ACTIVE]));
    }
    setError(null);
  }, [open, mode, addon]);

  const skuValid = useMemo(() => SKU_REGEX.test(sku) && sku !== Sku.BASE, [sku]);

  if (!open) return null;

  function close(): void {
    setError(null);
    onClose();
  }

  /** Validação local que espelha os schemas do callable; devolve a mensagem do 1º erro. */
  function validate(): string | null {
    if (mode === 'create') {
      if (sku.trim().length === 0) return t('billing.addons.errors.skuRequired');
      if (sku === Sku.BASE) return t('billing.addons.errors.skuReserved');
      if (!SKU_REGEX.test(sku)) return t('billing.addons.errors.skuInvalid');
    }
    if (!LOCALES.every((l) => nameI18n[l].trim().length >= 2)) {
      return t('billing.addons.errors.nameRequired');
    }
    if (!LOCALES.every((l) => descI18n[l].trim().length >= 10)) {
      return t('billing.addons.errors.descriptionRequired');
    }
    return null;
  }

  /**
   * Cria (`createAddon`) ou edita (`updateAddon`, com os países na mesma escrita) o add-on.
   *
   * Origem: sem issue — commit 1bc01f1b9, 2026-05-25 23:58 +0200 (Sprint 35, PR G1).
   *
   * Contrato: `create` → um callable; sucesso chama `onCreated(sku)`. `edit` →
   * UM `updateAddon` com i18n, capabilities e, se o conjunto mudou,
   * `enabled_countries` (#935, item 8). Se o servidor não ecoar
   * `countries_applied` (Functions ainda antigas no release — o Hosting sai
   * antes), cai de volta no `setAddonEnabledCountries` em segunda chamada, como
   * era até 2026-10-13. Erro por `err.code`: `already-exists` → SKU existe;
   * `not-found`; resto → genérico, e o erro vai ao PostHog.
   *
   * Dados — input do `createAddon` (exemplo):
   *   { "sku": "MODULE_X", "name_i18n": { "pt-BR": "…", "en": "…", "es": "…",
   *     "it": "…" }, "description_i18n": { … }, "enabled_countries": ["*"],
   *     "required_capabilities": ["BASE_ACTIVE"] }
   *
   * Usado por: botão "Salvar" deste diálogo.
   *
   * Falhas conhecidas: #710 (auditoria F3) — erro lido da mensagem; ver abaixo.
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   *  - 2026-10-13 11:10 +0200 — (#935, item 8) uma chamada só.
   */
  async function submit(): Promise<void> {
    const errMsg = validate();
    if (errMsg) {
      setError(errMsg);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'create') {
        const out = await createAddonCallable({
          sku,
          name_i18n: nameI18n,
          description_i18n: descI18n,
          enabled_countries: Array.from(countries),
          required_capabilities: Array.from(capabilities) as Capability[],
        });
        toast.success(t('billing.addons.success.created'));
        onCreated?.(out.sku);
        close();
      } else {
        if (!addon) return;
        const beforeSet = new Set(addon.enabled_countries);
        const diff =
          beforeSet.size !== countries.size ||
          Array.from(countries).some((c) => !beforeSet.has(c));
        // Bug corrigido em 2026-10-13 11:10 +0200 (#935, item 8): eram duas
        // chamadas (`updateAddon` e depois `setAddonEnabledCountries`); se a
        // segunda falhasse, nome/capabilities ficavam gravados e os países
        // não. Agora os países vão na mesma escrita.
        const out = await updateAddonCallable({
          sku: addon.sku,
          name_i18n: nameI18n,
          description_i18n: descI18n,
          required_capabilities: Array.from(capabilities) as Capability[],
          ...(diff ? { enabled_countries: Array.from(countries) } : {}),
        });
        // Servidor antigo (o Hosting publica antes das Functions) descarta
        // `enabled_countries` em silêncio e não ecoa `countries_applied` — sem
        // este fallback a mudança de países se perderia nessa janela.
        if (diff && out.countries_applied !== true) {
          await setAddonEnabledCountriesCallable({
            sku: addon.sku,
            countries: Array.from(countries),
          });
        }
        if (diff) {
          toast.success(t('billing.addons.success.countriesUpdated'));
        } else {
          toast.success(t('billing.addons.success.updated'));
        }
        close();
      }
    } catch (err) {
      // O discriminador do `HttpsError` está em `err.code`
      // (`functions/already-exists`), não na mensagem (o `CouponEditorDialog`,
      // que seguia o mesmo padrão, já não existe). Lendo a mensagem, `skuExists`
      // nunca aparecia (#710, auditoria).
      const code =
        typeof (err as { code?: unknown }).code === 'string'
          ? (err as { code: string }).code
          : '';
      if (code.endsWith('already-exists')) {
        setError(t('billing.addons.errors.skuExists'));
      } else if (code.endsWith('not-found')) {
        setError(t('billing.addons.errors.notFound'));
      } else {
        setError(
          mode === 'create'
            ? t('billing.addons.errors.create')
            : t('billing.addons.errors.update'),
        );
      }
      // #935 (item 14): o erro ia só ao console, que não chega até nós.
      reportNonFatal('admin_addon_submit_failed', { mode, code, ...serializeError(err) });
    } finally {
      setSubmitting(false);
    }
  }

  const title =
    mode === 'create'
      ? t('billing.addons.dialog.titleCreate')
      : t('billing.addons.dialog.titleEdit', {
          name: addon?.name_i18n['pt-BR'] ?? addon?.sku ?? '',
        });

  return (
    <Modal
      visible={open}
      onDismiss={close}
      header={title}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('billing.addons.dialog.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void submit();
              }}
              loading={submitting}
            >
              {mode === 'create'
                ? t('billing.addons.dialog.submitCreate')
                : t('billing.addons.dialog.submitEdit')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {/* `stretch`: sem ele o Cloudscape deixa o campo em 2/3 da largura e ele
            desalinha da grade de idiomas abaixo (#954). */}
        <FormField
          stretch
          label={t('billing.addons.dialog.sku')}
          description={t('billing.addons.dialog.skuHint')}
        >
          <Input
            value={sku}
            onChange={({ detail }): void => {
              setSku(detail.value.toUpperCase());
            }}
            placeholder={t('billing.addons.dialog.skuPlaceholder')}
            disabled={mode === 'edit' || submitting}
            invalid={mode === 'create' && sku.length > 0 && !skuValid}
          />
        </FormField>

        <SpaceBetween size="m">
          <Header variant="h3" description={t('billing.addons.dialog.textsHint')}>
            {t('billing.addons.dialog.texts')}
          </Header>
          {/* #954: cada idioma é um bloco com rótulo visível — antes os 4 campos
              só tinham placeholder (e a descrição nem isso), e não dava para
              saber qual idioma se editava depois de preenchido. Grade 2×2 que o
              ColumnLayout colapsa para 1 coluna em tela estreita. */}
          <ColumnLayout columns={2}>
            {LOCALES.map((l) => {
              const lang = t(LOCALE_LABEL_KEY[l]);
              return (
                <SpaceBetween key={l} size="xs">
                  <Box variant="h4">{lang}</Box>
                  <FormField label={t('billing.addons.dialog.name')}>
                    <Input
                      value={nameI18n[l]}
                      onChange={({ detail }): void => {
                        setNameI18n((prev) => ({ ...prev, [l]: detail.value }));
                      }}
                      disabled={submitting}
                      ariaLabel={`${t('billing.addons.dialog.name')} — ${lang}`}
                    />
                  </FormField>
                  <FormField label={t('billing.addons.dialog.description')}>
                    <Textarea
                      value={descI18n[l]}
                      onChange={({ detail }): void => {
                        setDescI18n((prev) => ({ ...prev, [l]: detail.value }));
                      }}
                      disabled={submitting}
                      rows={3}
                      ariaLabel={`${t('billing.addons.dialog.description')} — ${lang}`}
                    />
                  </FormField>
                </SpaceBetween>
              );
            })}
          </ColumnLayout>
        </SpaceBetween>

        <SpaceBetween size="m">
          <Header variant="h3" description={t('billing.addons.dialog.countriesHint')}>
            {t('billing.addons.dialog.countries')}
          </Header>
          <AddonCountriesPicker value={countries} onChange={setCountries} disabled={submitting} />
        </SpaceBetween>

        {mode === 'create' && (
          <Alert type="warning" header={t('billing.addons.dialog.willHappen')}>
            <SpaceBetween size="xxs">
              <Box>1. {t('billing.addons.dialog.willHappenStep1', { sku: sku.toLowerCase() || 'sku' })}</Box>
              <Box>2. {t('billing.addons.dialog.willHappenStep2', { sku: sku || 'SKU' })}</Box>
              <Box>3. {t('billing.addons.dialog.willHappenStep3', { sku: sku || 'SKU' })}</Box>
              <Box>{t('billing.addons.dialog.willHappenWarning')}</Box>
            </SpaceBetween>
          </Alert>
        )}

        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
